// بِسْمِ ٱللَّهِ ٱلرَّحْمَٰنِ ٱلرَّحِيمِ

import { useAuthStore } from '../stores/authStore';

// بِسْمِ ٱللَّهِ ٱلرَّحْمَٰنِ ٱلرَّحِيمِ
// Production API URL - если переменная окружения не установлена, используем production URL
const API_URL = (import.meta.env?.VITE_API_URL as string) || 'https://v2-summio-admin.unilife-ch.workers.dev';

class ApiService {
  private getHeaders(): HeadersInit {
    const token = useAuthStore.getState().token;
    return {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
    };
  }

  async login(username: string, password: string) {
    const response = await fetch(`${API_URL}/admin/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username, password }),
    });

    if (!response.ok) {
      throw new Error('Login failed');
    }

    return response.json();
  }

  async logout() {
    const response = await fetch(`${API_URL}/admin/auth/logout`, {
      method: 'POST',
      headers: this.getHeaders(),
    });

    if (!response.ok) {
      throw new Error('Logout failed');
    }

    return response.json();
  }

  async getUsers(params: {
    page?: number;
    limit?: number;
    search?: string;
    tier?: string;
  }) {
    const query = new URLSearchParams();
    if (params.page) query.append('page', params.page.toString());
    if (params.limit) query.append('limit', params.limit.toString());
    if (params.search) query.append('search', params.search);
    if (params.tier) query.append('tier', params.tier);

    const response = await fetch(`${API_URL}/admin/users?${query}`, {
      headers: this.getHeaders(),
    });

    if (!response.ok) {
      throw new Error('Failed to fetch users');
    }

    return response.json();
  }

  async updateUser(userId: string, updates: any) {
    const response = await fetch(`${API_URL}/admin/users/${userId}`, {
      method: 'PUT',
      headers: this.getHeaders(),
      body: JSON.stringify(updates),
    });

    if (!response.ok) {
      throw new Error('Failed to update user');
    }

    return response.json();
  }

  async banUser(userId: string) {
    const response = await fetch(`${API_URL}/admin/users/${userId}/ban`, {
      method: 'POST',
      headers: this.getHeaders(),
    });

    if (!response.ok) {
      throw new Error('Failed to ban user');
    }

    return response.json();
  }

  async getDashboardStats() {
    const response = await fetch(`${API_URL}/admin/stats`, {
      headers: this.getHeaders(),
    });

    if (!response.ok) {
      throw new Error('Failed to fetch stats');
    }

    return response.json();
  }

  async getSummaries(params: {
    page?: number;
    limit?: number;
    search?: string;
  }) {
    const query = new URLSearchParams();
    if (params.page) query.append('page', params.page.toString());
    if (params.limit) query.append('limit', params.limit.toString());
    if (params.search) query.append('search', params.search);

    const response = await fetch(`${API_URL}/admin/summaries?${query}`, {
      headers: this.getHeaders(),
    });

    if (!response.ok) {
      throw new Error('Failed to fetch summaries');
    }

    return response.json();
  }

  async deleteSummary(summaryId: string) {
    const response = await fetch(`${API_URL}/admin/summaries/${summaryId}`, {
      method: 'DELETE',
      headers: this.getHeaders(),
    });

    if (!response.ok) {
      throw new Error('Failed to delete summary');
    }

    return response.json();
  }

  async getAnalytics(period: string) {
    const response = await fetch(`${API_URL}/admin/analytics?period=${period}`, {
      headers: this.getHeaders(),
    });

    if (!response.ok) {
      throw new Error('Failed to fetch analytics');
    }

    return response.json();
  }

  async generateAIUsers(count: number) {
    const response = await fetch(`${API_URL}/admin/ai/generate`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify({ count }),
    });

    if (!response.ok) {
      throw new Error('Failed to generate AI users');
    }

    return response.json();
  }

  async getRecentActivity(params: { limit?: number; offset?: number }) {
    const query = new URLSearchParams();
    if (params.limit) query.append('limit', params.limit.toString());
    if (params.offset) query.append('offset', params.offset.toString());

    const response = await fetch(`${API_URL}/admin/recent-activity?${query}`, {
      headers: this.getHeaders(),
    });

    if (!response.ok) {
      throw new Error('Failed to fetch recent activity');
    }

    return response.json();
  }

  async getUserDetail(userId: string) {
    const response = await fetch(`${API_URL}/admin/users/${userId}/detail`, {
      headers: this.getHeaders(),
    });

    if (!response.ok) {
      throw new Error('Failed to fetch user detail');
    }

    return response.json();
  }

  async cancelUserSubscription(userId: string) {
    const response = await fetch(`${API_URL}/admin/users/${userId}/subscription/cancel`, {
      method: 'POST',
      headers: this.getHeaders(),
    });

    if (!response.ok) {
      throw new Error('Failed to cancel subscription');
    }

    return response.json();
  }

  async updateUserSubscription(userId: string, data: {
    tier: 'free' | 'premium' | 'max';
    status?: 'active' | 'inactive' | 'cancelled' | 'trial';
    expires_at?: string;
  }) {
    const response = await fetch(`${API_URL}/admin/users/${userId}/subscription`, {
      method: 'PUT',
      headers: this.getHeaders(),
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      throw new Error('Failed to update subscription');
    }

    return response.json();
  }

  // Bulk Operations
  async bulkBanUsers(userIds: string[]) {
    const response = await fetch(`${API_URL}/admin/users/bulk/ban`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify({ userIds }),
    });

    if (!response.ok) {
      throw new Error('Failed to ban users');
    }

    return response.json();
  }

  async bulkUnbanUsers(userIds: string[]) {
    const response = await fetch(`${API_URL}/admin/users/bulk/unban`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify({ userIds }),
    });

    if (!response.ok) {
      throw new Error('Failed to unban users');
    }

    return response.json();
  }

  async bulkDeleteSummaries(summaryIds: string[]) {
    const response = await fetch(`${API_URL}/admin/summaries/bulk/delete`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify({ summaryIds }),
    });

    if (!response.ok) {
      throw new Error('Failed to delete summaries');
    }

    return response.json();
  }

  async getCategories() {
    const response = await fetch(`${API_URL}/admin/categories`, {
      headers: this.getHeaders(),
    });

    if (!response.ok) {
      throw new Error('Failed to fetch categories');
    }

    return response.json();
  }

  async createCategory(data: { name: string; description?: string; icon_emoji?: string }) {
    const response = await fetch(`${API_URL}/admin/categories`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      throw new Error('Failed to create category');
    }

    return response.json();
  }

  async updateCategory(categoryId: string, data: { name?: string; description?: string; icon_emoji?: string }) {
    const response = await fetch(`${API_URL}/admin/categories/${categoryId}`, {
      method: 'PUT',
      headers: this.getHeaders(),
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      throw new Error('Failed to update category');
    }

    return response.json();
  }

  async deleteCategory(categoryId: string) {
    const response = await fetch(`${API_URL}/admin/categories/${categoryId}`, {
      method: 'DELETE',
      headers: this.getHeaders(),
    });

    if (!response.ok) {
      throw new Error('Failed to delete category');
    }

    return response.json();
  }

  async getWorkerLogs(params: {
    worker: string;
    level?: 'error' | 'warn' | 'info' | 'debug';
    limit?: number;
    since?: string;
  }) {
    const query = new URLSearchParams();
    query.append('worker', params.worker);
    if (params.level) query.append('level', params.level);
    if (params.limit) query.append('limit', params.limit.toString());
    if (params.since) query.append('since', params.since);

    const response = await fetch(`${API_URL}/admin/logs/workers?${query}`, {
      headers: this.getHeaders(),
    });

    if (!response.ok) {
      throw new Error('Failed to fetch worker logs');
    }

    return response.json();
  }

  async getWorkersStatus() {
    const response = await fetch(`${API_URL}/admin/workers/status`, {
      headers: this.getHeaders(),
    });

    if (!response.ok) {
      throw new Error('Failed to fetch workers status');
    }

    return response.json();
  }

  // Push Notifications
  async sendPushNotification(payload: any) {
    const response = await fetch(`${API_URL}/admin/push-notifications/send`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      throw new Error('Failed to send push notification');
    }

    return response.json();
  }

  async getNotificationTemplates() {
    const response = await fetch(`${API_URL}/admin/push-notifications/templates`, {
      headers: this.getHeaders(),
    });

    if (!response.ok) {
      throw new Error('Failed to fetch notification templates');
    }

    return response.json();
  }

  async saveNotificationTemplate(template: { name: string; title: Record<string, string>; message: Record<string, string> }) {
    const response = await fetch(`${API_URL}/admin/push-notifications/templates`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify(template),
    });

    if (!response.ok) {
      throw new Error('Failed to save notification template');
    }

    return response.json();
  }

  async deleteNotificationTemplate(templateId: string) {
    const response = await fetch(`${API_URL}/admin/push-notifications/templates/${templateId}`, {
      method: 'DELETE',
      headers: this.getHeaders(),
    });

    if (!response.ok) {
      throw new Error('Failed to delete notification template');
    }

    return response.json();
  }

  async getNotificationHistory(params: { limit?: number; offset?: number }) {
    const query = new URLSearchParams();
    if (params.limit) query.append('limit', params.limit.toString());
    if (params.offset) query.append('offset', params.offset.toString());

    const response = await fetch(`${API_URL}/admin/push-notifications/history?${query}`, {
      headers: this.getHeaders(),
    });

    if (!response.ok) {
      throw new Error('Failed to fetch notification history');
    }

    return response.json();
  }

  // Reports Management
  async getReports(params: {
    page?: number;
    limit?: number;
    status?: string;
    entity_type?: string;
  }) {
    const query = new URLSearchParams();
    if (params.page) query.append('page', params.page.toString());
    if (params.limit) query.append('limit', params.limit.toString());
    if (params.status) query.append('status', params.status);
    if (params.entity_type) query.append('entity_type', params.entity_type);

    const response = await fetch(`${API_URL}/admin/reports?${query}`, {
      headers: this.getHeaders(),
    });

    if (!response.ok) {
      throw new Error('Failed to fetch reports');
    }

    return response.json();
  }

  async updateReport(reportId: string, data: {
    status?: 'pending' | 'reviewing' | 'resolved' | 'dismissed';
    resolution_note?: string;
  }) {
    const response = await fetch(`${API_URL}/admin/reports/${reportId}`, {
      method: 'PATCH',
      headers: this.getHeaders(),
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      throw new Error('Failed to update report');
    }

    return response.json();
  }

  // Feedback Management
  async getFeedback(params: {
    page?: number;
    limit?: number;
    category?: 'languages' | 'sciences';
  }) {
    const query = new URLSearchParams();
    if (params.page) query.append('page', params.page.toString());
    if (params.limit) query.append('limit', params.limit.toString());
    if (params.category) query.append('category', params.category);

    const response = await fetch(`${API_URL}/admin/feedback?${query}`, {
      headers: this.getHeaders(),
    });

    if (!response.ok) {
      throw new Error('Failed to fetch feedback');
    }

    return response.json();
  }

  async getFeedbackStats() {
    const response = await fetch(`${API_URL}/admin/feedback/stats`, {
      headers: this.getHeaders(),
    });

    if (!response.ok) {
      throw new Error('Failed to fetch feedback stats');
    }

    return response.json();
  }
}

export const apiService = new ApiService();
